import { Profiler, useEffect } from 'react'

import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { launchErrorPopUp } from '@src/components/modals/ErrorPopUpModal/index'
import * as PATH from '@src/configs/paths'
import { ConfigLoadingStatus } from '@src/enums'
import Layout from '@src/layouts/Layout'
import { ExchangePage, OrdersPage, StatsPage } from '@src/pages'
import EmbedWrapper from '@src/pages/Embed/EmbedWrapper'
import EmbedExchangePage from '@src/pages/Embed/Exchange'
import EmbedOrdersPage from '@src/pages/Embed/Orders'
import EmbedRewardsPage from '@src/pages/Embed/Rewards'
import { ExchangeDataProvider } from '@src/pages/Exchange/context/ExchangeDataContext'
import { ExchangeStateProvider } from '@src/pages/Exchange/context/ExchangeStateContext'
import FiatExchange from '@src/pages/FiatExchange'
import PointsPage from '@src/pages/Rewards'
import { logger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'

const PROFILE_EXCHANGE_PAGE = false

/**
 * Layout wrapper component that includes the Outlet for nested routes
 *
 * We use this component to apply the standard Mach.Exchange layout (with header/footer)
 * to all routes except the embed widget route. This ensures a consistent user experience
 * across the main application while allowing the embed route to have its own specialized
 * layout for third-party integration.
 */
const MainLayout = () => {
    return (
        <Layout>
            <div className='flex w-full flex-col items-center'>
                <div className='min-h-[max(2rem,calc((100vh-80px-700px)/3))]'></div>
                <Outlet />
            </div>
            <launchErrorPopUp.ErrorPopupWrapper />
        </Layout>
    )
}

const AppRoutes = () => {
    const getConfig = useConfigStore(state => state.getConfig)
    const loading = useConfigStore(state => state.loading)

    useEffect(() => {
        // Load configuration only once when the app mounts
        if (loading === ConfigLoadingStatus.loading_not_yet_started) {
            getConfig()
        }
    }, [])

    /**
     * We've structured our routes to separate the embed widget from the main application:
     *
     * 1. The /embed route uses its own dedicated layout (EmbedLayout) via the EmbedWrapper
     *    - This layout is optimized for rendering in iframes on third-party sites
     *    - It removes the standard header/footer and navigation elements
     *    - It applies customization from URL parameters (colors, branding, etc.)
     *    - It uses a mobile-only UI that works well in constrained spaces
     *
     * 2. All other routes use the standard Layout via MainLayout component
     *    - This maintains the full app experience with header, footer, navigation
     *    - It preserves the complete application context and features
     *
     * This separation allows us to:
     *   - Keep the widget focused and lightweight for embedding
     *   - Apply different styling rules to the widget vs main app
     *   - Maintain the full app experience for direct users
     *   - Prevent unwanted navigation in embedded contexts
     */
    return (
        <Routes>
            {/* Embed Widget Routes (uses EmbedLayout) */}
            <Route path={PATH.EMBED} element={<EmbedWrapper />} />
            <Route path={`${PATH.EMBED}/orders`} element={<EmbedOrdersPage />} />
            <Route path={`${PATH.EMBED}/exchange`} element={<EmbedExchangePage />} />
            <Route path={`${PATH.EMBED}/rewards`} element={<EmbedRewardsPage />} />

            {/* All other routes wrapped in standard Layout */}
            <Route element={<MainLayout />}>
                <Route
                    path={PATH.LANDING} // Instantly replace the app state to PATH.SWAP
                    element={<Navigate to={PATH.SWAP} replace />}
                />
                <Route
                    path={PATH.SWAP}
                    element={
                        PROFILE_EXCHANGE_PAGE ? (
                            <Profiler id='ExchangePage was' onRender={onRenderCallback}>
                                <ExchangeStateProvider>
                                    <ExchangeDataProvider>
                                        <ExchangePage />
                                    </ExchangeDataProvider>
                                </ExchangeStateProvider>
                            </Profiler>
                        ) : (
                            <ExchangeStateProvider>
                                <ExchangeDataProvider>
                                    <ExchangePage />
                                </ExchangeDataProvider>
                            </ExchangeStateProvider>
                        )
                    }
                />
                <Route path={PATH.ORDERS} element={<OrdersPage />} />
                <Route path={PATH.REWARDS} element={<PointsPage />} />
                <Route path={PATH.STATS} element={<StatsPage />} />
                <Route path={PATH.EXCHANGE} element={<FiatExchange />} />

                {/* catch-all route redirect to swap page */}
                <Route path='*' element={<Navigate to={PATH.LANDING} replace />} />
            </Route>
        </Routes>
    )
}

const onRenderCallback: React.ProfilerOnRenderCallback = (id: any, phase: any, actualDuration: any) => {
    logger.debug(`${id} rendered in ${phase} phase: ${actualDuration}ms`)
}

export default AppRoutes
